import { Member, UserProfile } from '../../components/common/ObjectTypes';
import { handleError } from '../apiUtils';
import { createAxiosInstance } from '../apiUtils';
import { ACCOUNT_DATA_STALE_MS, queryClient } from '../queryClient';
import { queryKeys } from '../queryKeys';
import { refreshMyDetails, refreshWorkspaces } from '../cacheRefresh';

// Toggle with REACT_APP_MOCK_USER_PROFILE=true in .env to exercise the Account
// page without a live backend. Double-gated: the env var must be "true" AND
// the build must be non-production, so an accidental flag in a prod build
// still hits the real API.
const USE_MOCK_USER_PROFILE =
  process.env.REACT_APP_MOCK_USER_PROFILE === 'true' &&
  process.env.NODE_ENV !== 'production';

const mockUserProfile: UserProfile = {
  id: 'mock-user-1',
  username: 'Atreyug',
  email: 'atreyug@earthranger.com',
  first_name: 'Atreyu',
  last_name: 'Greenskin',
  full_name: 'Atreyu Greenskin',
  is_superuser: true,
  accepted_eula: true,
  contact_email: 'atreyug@earthranger.com',
  account_setup_complete: true,
  workspaces: [
    {
      id: 'membership-1',
      workspace_id: '11111111-1111-1111-1111-111111111111',
      name: "Atreyu's Workspace",
      role: 'admin',
    },
    {
      id: 'membership-2',
      workspace_id: '22222222-2222-2222-2222-222222222222',
      name: 'Really long Workspace name goes here',
      role: 'admin',
    },
    {
      id: 'membership-3',
      workspace_id: '33333333-3333-3333-3333-333333333333',
      name: 'Read-only Workspace',
      role: 'viewer',
    },
    // Set workspace_id to "404" to exercise the not-found toast path on leave.
    {
      id: 'membership-stale',
      workspace_id: '404',
      name: 'Stale Workspace (404 on leave)',
      role: 'viewer',
    },
  ],
};

// Mutable copy mutated by the mocked PATCH/POST handlers so the UI reflects
// the same data the user just saved.
let mockProfileState: UserProfile = {
  ...mockUserProfile,
  workspaces: [...mockUserProfile.workspaces],
};

export async function getUserDetails(
  token: string,
  org_id: string,
  member_id: string,
) {
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.get(
      '/v2/organizations/' + org_id + '/members/' + member_id,
    );
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

export async function getEula(token: string) {
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.get('/v2/eula/');
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

export async function acceptEula(token: string) {
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.post('/v2/eula/accept/');
    refreshMyDetails();
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

export async function saveUserDetails(
  token: string,
  org_id: string,
  member_id: string,
  member: Member,
) {
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.put(
      '/v2/organizations/' + org_id + '/members/' + member_id + '/',
      member,
    );
    // Changing your own role changes what you can do in that workspace.
    refreshMyDetails();
    refreshWorkspaces();
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

// The signed-in user's profile, fetched once and shared by every caller (nav,
// account page, permissions, analytics, workspace pages). A missing answer
// (the API layer resolves a swallowed 401 to undefined) is not kept, so the
// next call after logging back in asks again.
// Typed `any` like the raw response it replaced, so existing callers keep compiling.
export async function getMyDetails(token: string): Promise<any> {
  const profile = await queryClient.fetchQuery(myDetailsQuery(token));
  if (profile === null) {
    queryClient.removeQueries({ queryKey: queryKeys.users.me() });
    return undefined;
  }
  return profile;
}

// Shared by getMyDetails and useMyDetails. Reused for ACCOUNT_DATA_STALE_MS; a
// null (missing) answer is stale at once, so it's asked again rather than kept.
export const myDetailsQuery = (token: string) => ({
  queryKey: queryKeys.users.me(),
  queryFn: async (): Promise<UserProfile | null> =>
    (await fetchMyDetails(token)) ?? null,
  staleTime: (query: { state: { data: unknown } }) =>
    query.state.data === null ? 0 : ACCOUNT_DATA_STALE_MS,
});

async function fetchMyDetails(token: string) {
  if (USE_MOCK_USER_PROFILE) {
    return Promise.resolve({
      ...mockProfileState,
      workspaces: [...mockProfileState.workspaces],
    });
  }
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.get('/v2/users/me/');
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

export async function updateMyProfile(
  token: string,
  data: { first_name?: string; last_name?: string; contact_email?: string },
) {
  if (USE_MOCK_USER_PROFILE) {
    mockProfileState = {
      ...mockProfileState,
      first_name: data.first_name ?? mockProfileState.first_name,
      last_name: data.last_name ?? mockProfileState.last_name,
      contact_email: data.contact_email ?? mockProfileState.contact_email,
    };
    refreshMyDetails();
    return Promise.resolve({
      ...mockProfileState,
      workspaces: [...mockProfileState.workspaces],
    });
  }
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.patch('/v2/users/me/', data);
    refreshMyDetails();
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}

export async function leaveWorkspace(
  token: string,
  workspaceId: string,
  membershipId: string,
) {
  if (USE_MOCK_USER_PROFILE) {
    // The fixture workspace with workspace_id "404" triggers the not-found
    // toast path so the UI can be exercised end-to-end without a backend.
    if (workspaceId === '404') {
      return Promise.reject({ response: { status: 404 } });
    }
    mockProfileState = {
      ...mockProfileState,
      workspaces: mockProfileState.workspaces.filter(
        (w) => w.workspace_id !== workspaceId,
      ),
    };
    refreshMyDetails();
    return Promise.resolve({});
  }
  const axiosInstance = await createAxiosInstance(token);
  try {
    const resp = await axiosInstance.post(
      '/v2/organizations/' + workspaceId + '/members/remove/',
      { member_ids: [membershipId] },
    );
    refreshMyDetails();
    refreshWorkspaces();
    return resp.data;
  } catch (err) {
    handleError(err);
  }
}
